import { QbTagCategory } from '@exam-room/graphql';
import { useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  BookmarkBorderOutlinedIcon,
  BookmarkIcon,
  Form,
  Question,
  QuestionLayout,
} from '../../../components';
import { Choices, ExamReview, ExamReviewHeader } from '../../../containers';
import { useExamPreviewSettings, useNewExam } from '../../../providers';
import { type PreviewQuestionMetadata } from '../../../providers/exam-preview/types';
import { PATHS } from '../../constants';
import { QuestionHeader, QuestionSolution } from '../components';
import { ReviewResultSection } from '../containers';

import { ExamPreviewReviewFooter } from './containers';
import { getQuestionChipType } from './utils';

function ExamRoomPreview() {
  const { t } = useTranslation();
  const { taskId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({});
  const q = searchParams.get('q') ?? '';
  const page = searchParams.get('page') ?? '';

  const { questions, questionMap } = useNewExam();
  const { previewSettings } = useExamPreviewSettings();
  const { shouldDisplayAnswer } = previewSettings;

  const currentQuestion = questionMap[q] ?? {};

  const { question, order, metadata, tags } = currentQuestion;

  const { choices = [], correctAnswer = { value: '' } }: PreviewQuestionMetadata = metadata ?? {};

  let article: React.ReactNode;

  const enabledHighlight = false; // Keep it because the highlight feature is still here

  const tag = tags?.find((tag) => tag?.category === QbTagCategory.Part);

  const examRoomActions: JSX.Element[] = [
    <Form.CustomCheckbox name={`${q}.isBookmark`} id={`${q}.isBookmark`} key={`Bookmark.${q}`}>
      {({ open }) => (
        <div
          className={cn(
            'border-text-disabled text-body2 text-text-primary flex items-center gap-1.5 rounded-sm border px-3 py-1.5 leading-5',
            open && 'border-warning bg-warning-100',
          )}
        >
          {open ? (
            <BookmarkIcon className='text-warning-500 text-xl' />
          ) : (
            <BookmarkBorderOutlinedIcon className='text-xl' />
          )}
          <p className={cn('text-text-primary', open && 'text-warning-700')}>
            {t('examRoom.action.bookmark')}
          </p>
        </div>
      )}
    </Form.CustomCheckbox>,
  ];

  useEffect(() => {
    const validQuestionId = q && (questionMap[q] || page == 'review');

    if (questions && !validQuestionId) {
      const params = new URLSearchParams();
      params.set('q', questions[0]?.id);
      params.set('page', 'exam');

      const query = params.toString();
      setSearchParams(query);
    }
  }, [questions]);

  const isReviewPage = page === 'review';

  if (!Object.keys(currentQuestion).length && !isReviewPage) {
    return null;
  }

  const {
    refCode = '',
    calculation = '',
    difficulty = '',
    solution = '',
    reference = '',
    learningObjectiveStatement = '',
  } = currentQuestion?.metadata ?? {};

  const questionType = {
    type: getQuestionChipType(currentQuestion.metadata.questionGroupType),
    questionSize: currentQuestion?.metadata?.size,
  };

  const correctChoice = choices.find((choice) => choice.value === correctAnswer.value) ?? {
    value: '',
    label: '',
    order: 0,
  };

  return (
    <div className={cn('flex h-full w-full flex-1 flex-col items-center gap-6')}>
      {isReviewPage ? (
        <div className='flex h-full w-full max-w-4xl flex-col items-center gap-5 rounded-2xl bg-white p-10'>
          <ExamReviewHeader />
          <ExamReview />
          <ExamPreviewReviewFooter />
        </div>
      ) : (
        <>
          <QuestionHeader
            calculation={calculation}
            difficulty={difficulty}
            refCode={refCode}
            questionType={questionType}
            variant={shouldDisplayAnswer ? 'details' : 'summary'}
          />
          <div
            className={cn(
              'flex h-full w-full flex-1 overflow-auto rounded-2xl bg-white shadow-sm',
              !enabledHighlight ? 'select-none' : 'select-text',
              isReviewPage && 'mx-auto max-w-3xl',
            )}
          >
            <QuestionLayout
              id='exam-highlight-section'
              article={article}
              className={cn(!article && 'min-w-[576px]')}
            >
              <div className='flex h-full w-full'>
                <Question
                  key={`Question.${q}`}
                  number={order}
                  title={question}
                  name={`${q}.answer`}
                  choices={<Choices name={`${q}.answer`} choices={metadata?.choices} />}
                  actions={examRoomActions}
                  className={enabledHighlight ? 'select-text' : ''}
                  tag={tag?.name ?? ''}
                  paragraphs={metadata?.paragraphs}
                />
              </div>
            </QuestionLayout>
          </div>
          {shouldDisplayAnswer ? (
            <QuestionSolution
              correctAnswer={{
                value: correctChoice.value,
                label: correctChoice.label,
              }}
              solution={solution}
              reference={reference}
              los={learningObjectiveStatement}
            />
          ) : null}
          <ReviewResultSection q={q} taskId={taskId ?? ''} />
        </>
      )}
    </div>
  );
}

const ExamRooPreviewRoute = {
  path: PATHS.Preview.Root,
  element: <ExamRoomPreview />,
};

export default ExamRooPreviewRoute;
